import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import {
  useGetProfilesForCaseQuery,
  useBulkUnlinkChildProfilesMutation,
  useUpdateCaseProfileOwnersOrControllersCompletedMutation,
} from "../../graphql/supergraphHooks";
import {
  type AccountTypeEnum as AccountType,
  type ProfileType,
} from "../../graphql/supergraphTypes";
import { useGuidedProfileCreation } from "../../hooks/featureFlags";
import {
  isIndividualAccountType,
  isJoinAccountType,
  resolvedAccountType,
} from "../../utils/account_types";
import { clientContext } from "../../utils/graphql_utils";
import { useUpdateProfile } from "../profile/hooks";
import { provideProfileName } from "../ProfileTree/utils";
import { AccountTypeFormContent } from "./AccountTypeFormContent";
import { createAccountTypeSchema } from "./schemas";
import { AccountFormValues } from "./types";
import {
  prepareIndividualPayload,
  prepareInstitutionPayload,
  prepareJointPayload,
  isCategoryChanged,
  getAllAffectedDescendantProfiles,
} from "./utils";

export const AccountTypeForm = () => {
  const { caseId } = useComplianceCaseContext();
  const history = useHistory();
  const { basePath } = useComplianceCaseContext();
  const guidedProfileCreation = useGuidedProfileCreation();
  const [showExtendedTypes, setShowExtendedTypes] = useState(false);

  const caseResult = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "cache-and-network",
    variables: {
      caseId: caseId,
    },
  });

  const profile = useMemo(
    () => caseResult.data?.complianceCase?.investingProfile,
    [caseResult.data?.complianceCase?.investingProfile],
  );

  const profiles = useMemo(
    () =>
      (caseResult.data?.complianceCase?.profiles || []).filter(
        (p) => p !== null,
      ) as ProfileType[],
    [caseResult.data?.complianceCase?.profiles],
  );

  const profileId = useMemo(() => String(profile?.id ?? ""), [profile?.id]);

  const affectedChildProfiles = useMemo(
    () => getAllAffectedDescendantProfiles(profiles, profileId),
    [profiles, profileId],
  );

  const { updateIndividual, updateInstitution, updateJoint, isUpdating } = useUpdateProfile(
    profile?.id || 0,
    null,
    () => history.push(`${basePath}`),
  );

  const [bulkUnlinkChildProfiles] = useBulkUnlinkChildProfilesMutation({
    refetchQueries: ["GetProfilesForCase"],
    awaitRefetchQueries: true,
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const [updateOwnersOrControllersCompleted] =
    useUpdateCaseProfileOwnersOrControllersCompletedMutation({
      refetchQueries: ["GetComplianceCase", "GetProfilesForCase", "GetProfileActionInfo"],
      awaitRefetchQueries: true,
    });

  const handleSubmit = async (accountType: AccountType) => {
    const profileId = profile?.id ?? -1;
    const isCategoryChange = isCategoryChanged(profile?.accountType ?? undefined, accountType);

    if (isCategoryChange) {
      await updateOwnersOrControllersCompleted({
        variables: {
          caseId,
          profileId,
          ownersCompleted: false,
          controllersCompleted: false,
          documentsCompleted: false,
        },
      });
      if (affectedChildProfiles.length > 0) {
        await bulkUnlinkChildProfiles({
          variables: { profileId, caseId },
        });
      }
    } else {
      await updateOwnersOrControllersCompleted({
        variables: {
          caseId,
          profileId,
          documentsCompleted: false,
        },
      });
    }

    if (isIndividualAccountType(accountType)) {
      await updateIndividual(prepareIndividualPayload(caseId, { accountType }, profile));
    } else if (guidedProfileCreation && isJoinAccountType(accountType)) {
      await updateJoint(prepareJointPayload(caseId, { accountType }, profile));
    } else {
      await updateInstitution(prepareInstitutionPayload(caseId, { accountType }, profile));
    }
  };

  if (!profile) {
    return null;
  }

  return (
    <Form<AccountFormValues>
      initialValues={{
        accountType: resolvedAccountType(profile),
      }}
      onSubmit={async (values: AccountFormValues) => {
        await handleSubmit(values.accountType!);
      }}
      yupValidationSchema={createAccountTypeSchema(showExtendedTypes)}
      submitOnlyChangedValues={false}
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <AccountTypeFormContent
        accountName={provideProfileName(profile)}
        isControllerMode={false}
        isLoading={isUpdating}
        onExtendedTypesToggle={setShowExtendedTypes}
        profiles={profiles}
        profileId={profileId}
      />
    </Form>
  );
};
